import {
  MILLISECONDS_PER_DAY,
  MONGODB_ATTRIBUTES_DAYS_PAST,
} from "./keys";

// - - - - - - - - - -
// Pure week-date helpers used by per-grid week navigation (#105 phase 3).
// Extracted from app.context.js so each grid can manage its own week locally
// without touching shared state. Same arithmetic as the AppContext versions:
// 12-hour offset on subtractions to avoid daylight-saving boundary off-by-one.

/**
 * Returns the Monday of the week containing `date` as a Date object at 00:00.
 */
export const getMondayOf = (date) => {
  const d = new Date(date);
  const dayOfWeek = d.getDay(); // sun=0, mon=1, tue=2, ...
  const diff = (1 - dayOfWeek) * MILLISECONDS_PER_DAY; // sun=+1, mon=0, tue=-1
  return new Date(d.getTime() + diff);
};

/**
 * Returns the Monday of the current real-world week.
 */
export const getMondayOfThisWeek = () => getMondayOf(new Date());

/**
 * Given a Monday, returns the Monday 7 days earlier.
 * The +43200000 (12h) avoids DST-boundary off-by-one on subtraction.
 */
export const getPrevWeekMonday = (currentMonday) => {
  const ms =
    new Date(currentMonday).getTime() - MILLISECONDS_PER_DAY * 7 + 43200000;
  return new Date(ms);
};

/**
 * Given a Monday, returns the Monday 7 days later.
 */
export const getNextWeekMonday = (currentMonday) => {
  const ms = new Date(currentMonday).getTime() + MILLISECONDS_PER_DAY * 7;
  return new Date(ms);
};

/**
 * Builds the 7-element week array (en-US locale strings, mon..sun) from a
 * Monday Date. Mirrors AppContext.createWeekDatesArray exactly.
 */
export const buildWeekDatesArray = (mondayDate) => {
  const ms = new Date(mondayDate).getTime();
  return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
    new Date(ms + MILLISECONDS_PER_DAY * offset).toLocaleDateString("en-us")
  );
};

/**
 * True if navigating to `targetMondayDate` would go beyond the historical
 * data limit (MONGODB_ATTRIBUTES_DAYS_PAST days before today).
 */
export const isWeekBeyondLimit = (targetMondayDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoffDate = new Date(
    today.getTime() - MONGODB_ATTRIBUTES_DAYS_PAST * MILLISECONDS_PER_DAY
  );
  cutoffDate.setHours(0, 0, 0, 0);
  return new Date(targetMondayDate) < cutoffDate;
};

/**
 * True if navigating one week back from `currentWeekDatesArray` is allowed.
 */
export const canNavigateWeekBackFrom = (currentWeekDatesArray) => {
  if (!currentWeekDatesArray || currentWeekDatesArray.length !== 7) return false;
  const targetMonday = getPrevWeekMonday(currentWeekDatesArray[0]);
  return !isWeekBeyondLimit(targetMonday);
};
